<script setup lang="ts">
import { useProductStore } from '~/store/product/productStore';
import Reassurance from '~/components/product/Reassurance.vue';
import CarticleLarge from '~/components/product/CarticleLarge.vue';
import Cproduct from '~/components/product/Cproduct.vue';
import CrossSell from '~/components/sliders/CrossSell.vue';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import Breadcrumb from '~/components/breadcrumb/Breadcrumb.vue';
import CartRecap from '~/components/product/CartRecap.vue';
import Carticle from '~/components/articles/carticle.vue';
import LoadingBar from '~/components/loaders/LoadingBar.vue';
import MultiplePaymentsModal from '~/components/modals/MultiplePaymentsModal.vue';
import SizeGuideModal from '~/components/modals/SizeGuideModal.vue';
import ProductVisualizerModal from '~/components/modals/ProductVisualizerModal.vue';
import {
  MODAL_BRAND_VOUCHER,
  MODAL_CART,
  MODAL_CLOSE,
  MODAL_MULTIPLE_PAYMENT, MODAL_PRODUCT_AVAILABILITY,
  MODAL_PRODUCT_VISUALIZER, MODAL_PROMO_PRICE, MODAL_REVIEW_RULES,
  MODAL_SIZE_GUIDE,
  useModalStore
} from '~/store/modalStore';
import BrandVoucherModal from '~/components/modals/BrandVoucherModal.vue';
import PromoPriceDetailModal from '~/components/modals/PromoPriceDetailModal.vue';
import ProductAvailabilityModal from '~/components/modals/ProductAvailabilityModal.vue';
import ReviewRulesModal from '~/components/modals/ReviewRulesModal.vue';

const route = useRoute();
const productStore = useProductStore();
const modalStore = useModalStore();
const slugParam = route.params.slug;
const parts = slugParam.split('-');
const id = parts.pop();
const slug = parts.join('-');
const query = route.query;
const config = useRuntimeConfig()
const app = useNuxtApp()

const {
  product,
  isFullLoading,
  isOfferLoading,
  breadcrumb,
  productSEO,
  viewItemDatalayer,
  addToCartDatalayer
} = storeToRefs(productStore)

const {
  isCartModalVisible,
  isMultiplePaymentModalVisible,
  isProductVisualizerModalVisible,
  isSizeGuideModalVisible,
  isBrandVoucherModalVisible,
  isPromoPriceModalVisible,
  isProductAvailabilityModalVisible,
  isReviewRulesVisible,
  displayOverlay
} = storeToRefs(modalStore)

const get_gtm_container = () => {
  return config.public.gtmContainers[app.$i18n.locale]
};

useHead({
  title: productSEO.value.metaTitle,
  meta: [
    { name: 'description', content: productSEO.value.metaDescription }
  ],
  script: [
    {
      innerHTML: `
        window.dataLayer = window.dataLayer || [];
        dataLayer.unshift(${JSON.stringify(viewItemDatalayer.value)});
        dataLayer.unshift(${JSON.stringify(addToCartDatalayer.value)});
        (function (w, d, s, l, i) {
            w[l] = w[l] || [];
            w[l].push({
                'gtm.start':
                    new Date().getTime(), event: 'gtm.js'
            });
            var f = d.getElementsByTagName(s)[0],
                j = d.createElement(s), dl = l != 'dataLayer' ? '&l=' + l : '';
            j.async = true;
            j.src =
                'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
            f.parentNode.insertBefore(j, f);
        })(window, document, 'script', 'dataLayer', '${ get_gtm_container() }');
      `
    }
  ]
})

const pContent = ref();
const selectedTab = ref('')

await callOnce(async () => {
  await productStore.fetchProductData((route.params.sport as string), slug, id, query);
  productStore.id = id;
})

const modal = (name: string, action: string) => modalStore.displayModal(name, action);

const clickReviews = () => {
  selectedTab.value = 'reviews';
  pContent.value.scrollIntoView({ behavior: "smooth" });
}

onMounted(async () => {
  if (parseInt(id) !== product.value.id) {
    await productStore.fetchProductData((route.params.sport as string), slug, id, query);
    console.log('call');
  }

  productStore.id = id;
})
</script>

<template>
  <Suspense>
    <div>
      <div v-if="displayOverlay" class="coverlay coverlay--active"/>
      <loading-bar v-if="isFullLoading || isOfferLoading" />
      <div>
        <cart-recap
                   :close-modal="() => modal(MODAL_CART, MODAL_CLOSE)"
                   :is-modal-visible="isCartModalVisible"
        />
      </div>
      <div v-if="product" class="product-page">
        <div>
          <section class="product-page__wrapper mb-7">
            <div class="product-page__action-container">
              <product-actions @click-reviews="clickReviews" />
            </div>

            <div style="overflow: hidden" class="w100">
              <breadcrumb
                          :breadcrumb="breadcrumb"
                          :breadcrumbs-hidden="false" />
              <product-visualizer />
            </div>
            <div ref="pContent">
              <product-content :selected-tab="selectedTab"/>
            </div>
          </section>
        </div>
      </div>
      <div style="overflow: hidden">
        <div
            v-for="(crossSell, index) in product.crossSellCollection"
            :key="index"
            class="mb-2">
          <div v-if="crossSell" class="cwrapper cwrapper--no-mobile">
            <cross-sell
                v-if="crossSell.position !== 4"
                :title="crossSell.trackingInfos.title"
                :cross-sell-items="crossSell.crossSellItems">
              <div
                  v-for="(item, index2) in crossSell.crossSellItems"
                  :key="index2"
                  class="carousel-v2-item">
                <cproduct
                    :price-dto="item.priceDTO"
                    :sport="route.params.sport as string"
                    :product-id="item.id"
                    :main-image-image-jpg2x="item.mainImage.imageJPG2X"
                    :brand-name="item.brand.name"
                    :main-image-context="item.mainImage.context"
                    :main-image-image-webp2x="item.mainImage.imageWEBP2X"
                    :without-retina="true"
                    :label="item.label"
                    :global-rate-count="item.productReviewDTO.globalRateCount"
                    :name-without-brand="item.nameWithoutBrand"
                    :set-as-title="false"
                    :hide-label="false"
                    :global-rate="item.productReviewDTO.globalRate"/>
              </div>
            </cross-sell>
          </div>
        </div>
      </div>

      <div v-if="product.postDTOCollection.length > 0" class="overflow-wrapper mb-2">
        <section class="cwrapper cwrapper--no-mobile">
          <cross-sell
            :show-pagination="false"
            :title="$t('www.pdp.posts')"
            :cross-sell-items="product.postDTOCollection"
          >
            <div v-for="(post, index) in product.postDTOCollection" :key="index" class="carousel-v2-item">
              <carticle :post="post" />
            </div>
          </cross-sell>
        </section>
      </div>
      <div v-if="product.productQuestionCollection.length > 0" class="mb-3 bgc-grey-xxlight">
        <div class="cwrapper pt-3 pb-h">
          <product-questions
              :product-questions-collection="product.productQuestionCollection"
          />
        </div>
      </div>
      <div class="cwrapper">
        <carticle-large
                       :title="product.brand.name"
                       :button-text="$t('www.brand.all_products') + ' ' + product.brand.name"
                       :img="product.brand.logo"
                       :link="product.brand.url"
                       :description="product.brand.shortDescription" />
      </div>
      <div class="mb-3">
        <div class="cwrapper">
          <reassurance />
        </div>
      </div>
      <product-visualizer-modal :close-modal="() => modal(MODAL_PRODUCT_VISUALIZER, MODAL_CLOSE)" :is-modal-visible="isProductVisualizerModalVisible" />
      <multiple-payments-modal :close-modal="() => modal(MODAL_MULTIPLE_PAYMENT, MODAL_CLOSE)" :is-modal-visible="isMultiplePaymentModalVisible" />
      <size-guide-modal :close-modal="() => modal(MODAL_SIZE_GUIDE, MODAL_CLOSE)" :is-modal-visible="isSizeGuideModalVisible" />
      <brand-voucher-modal :close-modal="() => modal(MODAL_BRAND_VOUCHER, MODAL_CLOSE)" :is-modal-visible="isBrandVoucherModalVisible" />
      <promo-price-detail-modal :close-modal="() => modal(MODAL_PROMO_PRICE, MODAL_CLOSE)" :is-modal-visible="isPromoPriceModalVisible" />
      <product-availability-modal :close-modal="() => modal(MODAL_PRODUCT_AVAILABILITY, MODAL_CLOSE)" :is-modal-visible="isProductAvailabilityModalVisible" />
      <review-rules-modal :close-modal="() => modal(MODAL_REVIEW_RULES, MODAL_CLOSE)" :is-modal-visible="isReviewRulesVisible" />
    </div>
  </Suspense>
</template>

<style>
@media screen and (min-width: 769px) {
  .product-page__action-container {
    width: 425px !important;
  }
}
</style>
